import React, { useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, List, ListItem, ListItemButton,
  ListItemText, ListItemAvatar, Avatar, Chip, Stack, IconButton, Menu, MenuItem,
  ListItemIcon, Divider, Button, CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  LocalHospital as HospitalIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const statusColors = {
  New: 'info',
  Active: 'success',
  Inactive: 'default',
  Deceased: 'error',
  Transferred: 'warning',
};

function PatientList({
  patients = [],
  loading = false,
  onPatientSelect,
  onPatientEdit,
  onPatientDelete,
  onAddPatient,
  selectedPatient,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuPatient, setMenuPatient] = useState(null);

  const handleMenuOpen = (event, patient) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuPatient(patient);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuPatient(null);
  };

  const filteredPatients = patients.filter((patient) => !searchQuery
    || `${patient.firstName} ${patient.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    || (patient.email && patient.email.toLowerCase().includes(searchQuery.toLowerCase()))
    || (patient.phone && patient.phone.includes(searchQuery)));

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Patients (
            {filteredPatients.length}
            )
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={onAddPatient}
          >
            Add
          </Button>
        </Stack>

        <TextField
          placeholder="Search patients..."
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Patient List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredPatients.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <HospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="textSecondary">
              {searchQuery ? 'No patients found' : 'No patients yet. Add your first patient!'}
            </Typography>
            {!searchQuery && (
              <Button
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={onAddPatient}
                sx={{ mt: 2 }}
              >
                Add First Patient
              </Button>
            )}
          </Box>
        ) : (
          <List>
            {filteredPatients.map((patient) => (
              <ListItem
                key={patient._id}
                secondaryAction={(
                  <IconButton
                    edge="end"
                    onClick={(e) => handleMenuOpen(e, patient)}
                  >
                    <MoreIcon />
                  </IconButton>
                )}
                disablePadding
              >
                <ListItemButton
                  selected={selectedPatient?._id === patient._id}
                  onClick={() => onPatientSelect(patient)}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {patient.firstName?.[0]}
                      {patient.lastName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={(
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight="bold">
                          {patient.fullName || `${patient.firstName} ${patient.lastName}`}
                        </Typography>
                        {patient.flags && patient.flags.length > 0 && (
                          <WarningIcon fontSize="small" color="warning" />
                        )}
                      </Stack>
                    )}
                    secondary={(
                      <Stack spacing={0.5}>
                        {patient.age && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <CakeIcon fontSize="small" />
                            <Typography variant="caption">
                              {patient.age}
                              {' '}
                              years
                            </Typography>
                          </Stack>
                        )}
                        {patient.phone && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PhoneIcon fontSize="small" />
                            <Typography variant="caption">{patient.phone}</Typography>
                          </Stack>
                        )}
                        <Chip
                          label={patient.status || 'New'}
                          color={statusColors[patient.status] || 'default'}
                          size="small"
                          sx={{ width: 'fit-content', mt: 0.5 }}
                        />
                      </Stack>
                    )}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            onPatientEdit(menuPatient);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            onPatientDelete(menuPatient);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default PatientList;
